import { cloudSnapshot } from './cloudReportQueue.js'

const KEY = 'cmv-automatic-import-log'
const LABELS = {
  started: 'Automatic import activity started',
  file_selected: 'File selected',
  file_read: 'File parsed and columns checked',
  file_error: 'File could not be read',
  file_rejected: 'Import file rejected before review',
  preview_toggled: 'Optional file preview toggled',
  review_ready: 'Student matching and review prepared',
  review_error: 'Review preparation failed',
  step_opened: 'Wizard step opened',
  choice_changed: 'Review choice changed',
  bulk_changed: 'Bulk review choices changed',
  bulk_scope: 'Bulk choices applied to hidden students',
  choices_held: 'Search, filter, or collapse left choices unchanged',
  conflict_acknowledged: 'Flag conflict acknowledgment changed',
  ack_reset: 'Choice change requires acknowledgment again',
  navigation: 'Import workspace navigation',
  review_preserved: 'Review choices preserved on return',
  back_kept: 'Back kept review choices',
  layout: 'Review and action controls checked in the viewport',
  confirm_ready: 'Confirm step checked before import',
  import_started: 'Confirmed import started',
  import_finished: 'Import writer finished',
  import_error: 'Import failed',
  outcome_saved: 'Import outcome save finished',
  outcome_shown: 'Overview import status observed (not a persistence check)',
  outcome_readback: 'Overview outcome matches the school record read from the server',
  outcome_unavailable: 'Saved import outcome could not be verified',
  outcome_superseded: 'Saved import outcome check superseded by navigation or newer activity',
  session_cleared: 'School switch cleared the local import status',
  outcome_ignored: 'Result from a different school ignored by the overview',
  undo_batch_saved: 'Undo batch save finished',
  undo_batch_loaded: 'Stored Undo batch loaded',
  undo_batch_error: 'Undo batch could not be loaded',
  undo_cancelled: 'Restore confirmation cancelled',
  undo_started: 'Undo started',
  undo_finished: 'Undo writer finished',
  undo_error: 'Undo failed',
  undo_bookkeeping: 'Undo batch bookkeeping finished',
  undo_outcome_saved: 'Restore outcome save finished',
  write_readback: 'Written fields read back from the server',
  readback_unavailable: 'Server read-back could not be completed',
  readback_superseded: 'Server read-back superseded by a later operation',
  student_opened: 'Student editor reopened',
  editor_values: 'Reopened editor matches the written fields',
  school_changed: 'School changed; review reset',
  reset: 'Wizard reset',
  limit: 'Automatic log reached its event limit'
}
const ENUMS = {
  reason: ['goals-export', 'duplicate-columns', 'invalid-csv', 'missing-ssid', 'empty-file'],
  step: ['upload', 'pref', 'sep', 'acc', 'confirm', 'summary'],
  source: ['seis', 'aeries-schedule', 'aeries-contacts', 'aeries-504', 'overview'],
  view: ['import', 'setup'],
  decision: ['accept', 'keep', 'default'],
  operation: ['import', 'undo'],
  gesture: ['search', 'filter', 'collapse'],
  outcome: ['success', 'issues', 'restored', 'restore-issues', 'missing', 'unrecorded', 'unsaved'],
  code: ['permission-denied', 'unavailable', 'deadline-exceeded', 'unauthenticated', 'unknown']
}
const COUNTS = new Set([
  'rows',
  'students',
  'updated',
  'restored',
  'issues',
  'fields',
  'mismatches',
  'reviewHeight',
  'viewportWidth',
  'viewportHeight',
  'skipped',
  'changes',
  'applied',
  'visible',
  'hidden',
  'carried'
])
const FLAGS = new Set([
  'validColumns',
  'schoolBlocked',
  'checked',
  'tracked',
  'actionsVisible',
  'stepVisible',
  'previewOpen',
  'unchanged',
  'filePresent',
  'schoolMatches',
  'importAvailable',
  'matches',
  'kept'
])

export function parsedFileEvent(source, detail = {}) {
  if (detail.failed && ENUMS.reason.includes(detail.reason)) {
    return { event: 'file_rejected', values: { source, reason: detail.reason }, status: 'blocked' }
  }
  if (detail.failed) return { event: 'file_error', values: { source }, status: 'fail' }
  const rows = Number.isSafeInteger(detail.rows) && detail.rows >= 0 ? detail.rows : 0
  const validColumns = detail.validColumns === true
  const schoolBlocked = detail.schoolBlocked === true
  return {
    event: 'file_read',
    values: { source, rows, validColumns, schoolBlocked },
    status: rows > 0 && validColumns && !schoolBlocked ? 'pass' : 'blocked'
  }
}

export function bulkScopeEvent(applied, scope = {}) {
  const safeApplied = Number.isSafeInteger(applied) && applied >= 0 ? applied : 0
  const listed =
    Number.isSafeInteger(scope.listed) && scope.listed >= 0 ? scope.listed : safeApplied
  const visible = Number.isSafeInteger(scope.visible) && scope.visible >= 0 ? scope.visible : listed
  return {
    values: { applied: safeApplied, visible, hidden: Math.max(0, listed - visible) },
    status: safeApplied >= listed ? 'pass' : 'fail'
  }
}

// Deliberate allowlist: never accept error messages, file names, IDs, raw
// decisions, student objects or field values into a diagnostic event.
export function importActivityDetail(values = {}) {
  return Object.entries(values)
    .filter(([key, value]) =>
      ENUMS[key]
        ? ENUMS[key].includes(value)
        : COUNTS.has(key)
          ? Number.isSafeInteger(value) && value >= 0 && value <= 1000000
          : FLAGS.has(key) && typeof value === 'boolean'
    )
    .map(([key, value]) => `${key}=${value}`)
    .join('; ')
}

// One journal per browser tab/build/account. Persist the exact in-flight
// checkpoint, so a reload after a lost response retries the same revision.
// A journal stays open: a saved event is not a claim that the whole UI passed.
export function createImportActivityLog({
  send,
  getOwner,
  buildId,
  storage,
  onStatus = () => {},
  now = () => new Date().toISOString()
}) {
  let session, active
  const isFull = () => session?.report.results.length >= 501
  const newSession = (owner) => ({
    schema: 1,
    owner,
    buildId,
    revision: 0,
    acked: 0,
    pending: null,
    report: {
      runId: `smoke-import-auto-${now().replace(/[:.]/g, '-')}-${Math.random().toString(36).slice(2, 8)}`,
      startedAt: now(),
      user: {},
      results: []
    }
  })
  const persist = () => {
    try {
      storage?.setItem(KEY, JSON.stringify(session))
    } catch {
      /* memory still works */
    }
  }
  function ensure() {
    const owner = getOwner()
    if (!owner) return false
    if (session?.owner === owner) return true
    let saved
    try {
      saved = JSON.parse(storage?.getItem(KEY) || 'null')
    } catch {
      /* start fresh */
    }
    session =
      saved?.schema === 1 &&
      saved.owner === owner &&
      saved.buildId === buildId &&
      Array.isArray(saved.report?.results) &&
      Number.isInteger(saved.acked) &&
      Number.isInteger(saved.revision)
        ? saved
        : newSession(owner)
    return true
  }
  async function flush() {
    if (active) return active
    if (!ensure() || !session.report.results.length) return
    const current = session
    active = (async () => {
      try {
        onStatus({ runId: current.report.runId, status: 'saving' })
        while (current.pending || current.acked < current.report.results.length) {
          if (getOwner() !== current.owner) throw new Error('account-changed')
          if (!current.pending) {
            const snapshot = cloudSnapshot(current.report)
            current.pending = {
              runId: snapshot.runId,
              revision: current.revision + 1,
              state: 'running',
              stepCount: snapshot.steps.length,
              metadata: snapshot.metadata,
              steps: snapshot.steps.slice(current.acked, current.acked + 100)
            }
            persist()
          }
          await send(current.pending)
          current.acked += current.pending.steps.length
          current.revision = current.pending.revision
          current.pending = null
          persist()
        }
        if (session === current)
          onStatus({
            runId: current.report.runId,
            status: isFull() ? 'full' : 'saved',
            savedEvents: current.acked
          })
      } catch {
        if (session === current) onStatus({ runId: current.report.runId, status: 'error' })
      }
    })().finally(() => {
      active = null
      if (session !== current && session?.owner === getOwner()) void flush()
    })
    return active
  }
  function record(event, values = {}, status = 'pass') {
    if (!LABELS[event] || !['pass', 'fail', 'blocked', 'skip'].includes(status) || !ensure()) return
    if (isFull()) {
      // Also restore the full/error badge when resuming a capped journal
      // from sessionStorage. A stopped log must never look like active saving.
      void flush()
      return
    }
    if (session.report.results.length === 500) {
      event = 'limit'
      values = {}
      status = 'blocked'
    }
    session.report.results.push({
      scenario: 'importAutomaticActivity',
      step: LABELS[event],
      status,
      durationMs: 0,
      detail: `${now()} · ${event === 'started' ? `build=${buildId}; ` : ''}${importActivityDetail(values)}`
    })
    persist()
    void flush()
  }
  return {
    record,
    flush,
    restart: () => {
      if (
        !ensure() ||
        !isFull() ||
        active ||
        session.pending ||
        session.acked !== session.report.results.length
      )
        return false
      // Never discard an unacknowledged checkpoint. The prior journal stays
      // in Firebase, and the next one starts with its own id/revision sequence.
      session = newSession(getOwner())
      record('started')
      return true
    },
    hasSession: () => {
      const saved = (() => {
        try {
          return JSON.parse(storage?.getItem(KEY) || 'null')
        } catch {
          return null
        }
      })()
      return !!(
        (session || saved)?.report?.results?.length &&
        (session || saved).owner === getOwner() &&
        (session || saved).buildId === buildId
      )
    }
  }
}
